import React, { useState } from 'react';
import { FaToggleOn, FaToggleOff, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const OPERATIONS_CONFIG = [
  { flagField: 'MAC_Add', descField: 'MAC_DescrizioneAdd', label: 'Add', shortLabel: 'Add' },
  { flagField: 'MAC_Get', descField: 'MAC_DescrizioneGet', label: 'Get', shortLabel: 'Get' },
  { flagField: 'MAC_Src', descField: 'MAC_DescrizioneSrc', label: 'Src', shortLabel: 'Src' },
  { flagField: 'MAC_Srl', descField: 'MAC_DescrizioneSrl', label: 'Srl', shortLabel: 'Srl' },
  { flagField: 'MAC_Upd', descField: 'MAC_DescrizioneUpd', label: 'Upd', shortLabel: 'Upd' },
  { flagField: 'MAC_Set', descField: 'MAC_DescrizioneSet', label: 'Set', shortLabel: 'Set' },
  { flagField: 'MAC_Del', descField: 'MAC_DescrizioneDel', label: 'Del', shortLabel: 'Del' },
  { flagField: 'MAC_Rps', descField: 'MAC_DescrizioneRps', label: 'Rps', shortLabel: 'Rps' },
  { flagField: 'MAC_Prn', descField: 'MAC_DescrizionePrn', label: 'Prn', shortLabel: 'Prn' },
  { flagField: 'MAC_Syn', descField: 'MAC_DescrizioneSyn', label: 'Syn', shortLabel: 'Syn' },
];

const CrudOperationsGrid = ({ selectedRow, theme, onInputChange, isProcessing, inputTypes, compactMode = false }) => {
  const [isExpanded, setIsExpanded] = useState(!compactMode);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const themeColor = theme?.themeColor || 'emerald';

  if (!selectedRow) return null;

  // Helper per calcolare il numero di righe del testo
  const calculateRows = (text) => {
    if (!text) return 1;
    const lines = text.split('\n').length;
    const wrappedLines = Math.ceil(text.length / 50); // Stima caratteri per riga
    return Math.max(lines, Math.min(wrappedLines, 1));
  };

  // Helper per gestire l'espansione delle descrizioni
  const toggleDescriptionExpansion = (descField) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [descField]: !prev[descField]
    }));
  };

  // Helper per determinare se mostrare l'indicatore "clicca per vedere tutto"
  const shouldShowExpandIndicator = (descField) => {
    const text = selectedRow[descField] || '';
    const isExpanded = expandedDescriptions[descField];
    return text && text.length > 150 && !isExpanded; // Se il testo è lungo e non espanso
  };

  // Modalità compatta - PIÙ PICCOLA ma con pulsanti NORMALI
  if (compactMode && !isExpanded) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="flex items-center justify-between p-3">
          <div className="flex items-center space-x-2">
            <FaToggleOn className={`h-4 w-4 text-${themeColor}-600 dark:text-${themeColor}-400`} />
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Operazioni CRUD</h4>
          </div>
          <button
            type="button"
            onClick={() => setIsExpanded(true)}
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center transition-colors px-2 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <span className="mr-1">Espandi</span>
            <FaChevronDown className="h-3 w-3" />
          </button>
        </div>
        
        {/* Layout compatto con pulsanti NORMALI - colore viola scuro per tema scuro */}
        <div className="px-3 pb-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {OPERATIONS_CONFIG.map(op => (
              <button
                key={op.flagField}
                type="button"
                onClick={() => onInputChange(op.flagField, !selectedRow[op.flagField])}
                disabled={isProcessing}
                className={`
                  relative flex flex-col items-center justify-center p-2 rounded-md border transition-all duration-200 
                  ${selectedRow[op.flagField] 
                    ? `bg-${themeColor}-100 dark:bg-purple-900 border-${themeColor}-400 dark:border-purple-700 text-${themeColor}-700 dark:text-purple-200` 
                    : 'bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400'
                  } 
                  hover:scale-105 disabled:opacity-50 disabled:hover:scale-100 
                  focus:outline-none focus:ring-2 focus:ring-${themeColor}-500 focus:ring-offset-1
                  shadow-sm hover:shadow-md
                `}
                title={`${selectedRow[op.flagField] ? 'Disabilita' : 'Abilita'} ${op.label}`}
              >
                {/* Icona NORMALE - non gigante */}
                <div className="text-lg mb-1">
                  {selectedRow[op.flagField] ? <FaToggleOn /> : <FaToggleOff />}
                </div>
                
                {/* Label sotto l'icona */}
                <span className="text-xs font-medium whitespace-nowrap">
                  {op.shortLabel}
                </span>
                
                {/* Indicatore di stato piccolo - viola scuro per tema scuro */}
                <div className={`
                  absolute top-1 right-1 w-1.5 h-1.5 rounded-full transition-all duration-200
                  ${selectedRow[op.flagField] 
                    ? `bg-${themeColor}-500 dark:bg-purple-400` 
                    : 'bg-gray-300 dark:bg-gray-600'
                  }
                `} />
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Modalità espansa - RIDOTTA DELLA METÀ nelle dimensioni
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="flex items-center justify-between p-2 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2">
          <FaToggleOn className={`h-4 w-4 text-${themeColor}-600 dark:text-${themeColor}-400`} />
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Operazioni CRUD</h4>
        </div>
        {compactMode && (
          <button
            type="button"
            onClick={() => setIsExpanded(false)}
            className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center transition-colors px-1 py-0.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <span className="mr-1">Comprimi</span>
            <FaChevronUp className="h-2 w-2" />
          </button>
        )}
      </div>
      
      {/* Versione espansa RIDOTTA DELLA METÀ - pulsanti e padding molto più piccoli */}
      <div className="p-2">
        <div className="space-y-1.5">
          {OPERATIONS_CONFIG.map((op, index) => {
            const isDescExpanded = expandedDescriptions[op.descField];
            const descText = selectedRow[op.descField] || '';
            const isEmpty = !descText.trim();
            const shouldShowIndicator = shouldShowExpandIndicator(op.descField);
            
            return (
              <div 
                key={op.flagField} 
                className={`
                  flex items-start gap-2 p-1.5 rounded transition-colors
                  ${index % 2 === 0 ? 'bg-gray-50/50 dark:bg-gray-800/50' : 'bg-white dark:bg-gray-800'}
                  hover:bg-gray-100 dark:hover:bg-gray-700/50
                `}
              >
                {/* Operazione - più compatta */}
                <div className="w-8 pt-1">
                  <span className="text-xs font-medium text-gray-900 dark:text-gray-100">
                    {op.label}
                  </span>
                </div>
                
                {/* Toggle switch - MOLTO PIÙ PICCOLO */}
                <div className="w-10 pt-1">
                  <button
                    type="button"
                    onClick={() => onInputChange(op.flagField, !selectedRow[op.flagField])}
                    disabled={isProcessing}
                    className={`
                      inline-flex items-center justify-center w-6 h-3 rounded-full transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-${themeColor}-500 focus:ring-offset-1
                      ${selectedRow[op.flagField] 
                        ? `bg-${themeColor}-600 dark:bg-${themeColor}-500` 
                        : 'bg-gray-300 dark:bg-gray-600'
                      } 
                      disabled:opacity-50 hover:scale-105 disabled:hover:scale-100
                    `}
                  >
                    <div className={`
                      w-2 h-2 rounded-full bg-white transition-transform duration-200 shadow-sm
                      ${selectedRow[op.flagField] ? 'translate-x-1' : '-translate-x-1'}
                    `} />
                  </button>
                </div>
                
                {/* Descrizione - SEMPLIFICATA per evitare problemi */}
                <div className="flex-1">
                  <div className="relative">
                    <textarea
                      value={descText}
                      onChange={(e) => {

                        if (onInputChange) {
                          onInputChange(op.descField, e.target.value);
                        }
                      }}
                      disabled={isProcessing}
                      rows={
                        isEmpty 
                          ? 1 
                          : isDescExpanded 
                            ? Math.max(3, calculateRows(descText))
                            : descText ? 3 : 1
                      }
                      className={`
                        w-full px-1.5 py-0.5 text-xs border rounded transition-all duration-200
                        ${isProcessing 
                          ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' 
                          : 'bg-white dark:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500'
                        }
                        border-gray-300 dark:border-gray-600 
                        text-gray-900 dark:text-gray-100 
                        placeholder-gray-400 dark:placeholder-gray-500
                        focus:outline-none focus:ring-1 focus:ring-${themeColor}-500 focus:border-${themeColor}-500
                        disabled:opacity-50
                        ${!isDescExpanded && descText ? 'resize-none overflow-hidden' : 'resize-vertical'}
                      `}
                      placeholder={`Desc. ${op.label}...`}
                      style={{
                        minHeight: isEmpty ? '2rem' : isDescExpanded ? '4rem' : '3rem'
                      }}
                    />
                    
                    {/* Indicatore per espandere la descrizione */}
                    {shouldShowIndicator && (
                      <div 
                        className="absolute bottom-0 right-0 bg-gradient-to-l from-white dark:from-gray-800 to-transparent px-2 py-1 cursor-pointer"
                        onClick={() => toggleDescriptionExpansion(op.descField)}
                      >
                        <span className="text-xs text-gray-500 dark:text-gray-400 italic">
                          Clicca per vedere tutto...
                        </span>
                      </div>
                    )}
                    
                    {/* Pulsante per comprimere quando espanso */}
                    {isDescExpanded && descText && (
                      <button
                        type="button"
                        onClick={() => toggleDescriptionExpansion(op.descField)}
                        className="absolute top-1 right-1 text-xs text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 bg-white dark:bg-gray-800 rounded px-1"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CrudOperationsGrid;